/**
 * The trade ticket's presentational layer: renders a {@link TicketVM} and reports taps through
 * {@link TicketActions}. No SDK, no data fetching, no global state — the live container
 * (components/TradeTicket.tsx) and the dev review page (/dev/ticket) both drive it.
 *
 * Spec: docs/ticket-ux.md. Layout on open is deliberately simple — outcome · venue · stake ·
 * pay/win · the button — with everything else under "More options".
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { StakeUnit } from "@openmarketsai/order-sheet";
import type { FixAction, FixVM, OverlayVM, ProblemVM, ResultVM, StepVM, TicketVM, VenueRowVM } from "../../lib/ticketModel.js";
import { generalProblems, problemAt } from "../../lib/ticketModel.js";
import { VenueLogo } from "../VenueLogo.js";
import { Skeleton } from "../ui.js";

export interface TicketActions {
  onClose: () => void;
  onStakeText: (text: string) => void;
  onChip: (value: number) => void;
  onUnit: (unit: StakeUnit) => void;
  onPriceText: (text: string) => void;
  onFix: (action: FixAction) => void;
  onOrderType: (t: "market" | "limit") => void;
  onVerify: (on: boolean) => void;
  onPlace: () => void;
  onConfirm: () => void;
  onBack: () => void;
  onPlaceAnother: () => void;
  /** Done → Portfolio (filled / partial / cancelled). Fly from `from`. */
  onDone: (from: HTMLElement | null) => void;
  onLeaveWorking: (from: HTMLElement | null) => void;
  onCancelResting: () => void;
  onRetry: (price: number) => void;
  onTryRest: (amount: number, price: number) => void;
  onEdit: () => void;
  onCheckAgain: () => void;
}

export const NOOP_ACTIONS: TicketActions = {
  onClose: () => {},
  onStakeText: () => {},
  onChip: () => {},
  onUnit: () => {},
  onPriceText: () => {},
  onFix: () => {},
  onOrderType: () => {},
  onVerify: () => {},
  onPlace: () => {},
  onConfirm: () => {},
  onBack: () => {},
  onPlaceAnother: () => {},
  onDone: () => {},
  onLeaveWorking: () => {},
  onCancelResting: () => {},
  onRetry: () => {},
  onTryRest: () => {},
  onEdit: () => {},
  onCheckAgain: () => {},
};

export interface TicketViewProps {
  vm: TicketVM;
  actions: TicketActions;
  /** Mobile bottom sheet (bigger targets, no header bar — the sheet has its own). */
  mobile: boolean;
  /** Rendered above the button (greenbook's unlock card for a plan/grant refusal). */
  unlockSlot?: ReactNode;
  /** Dev page: start with these expanders open. */
  initialOpen?: { options?: boolean; details?: boolean };
  /** "View market" (the Market screen for this book); absent = no link. */
  marketHref?: string;
}

export function TicketView({ vm, actions, mobile, unlockSlot, initialOpen, marketHref }: TicketViewProps) {
  const [optionsOpen, setOptionsOpen] = useState(!!initialOpen?.options);
  const priceProblem = problemAt(vm.problems, "price");
  // A price problem sits under the max-price field, which lives under More options: open it so
  // the message isn't anchored to something hidden.
  useEffect(() => {
    if (priceProblem && priceProblem.tone === "block") setOptionsOpen(true);
  }, [priceProblem?.key, priceProblem?.tone]);

  const onFix = (a: FixAction) => {
    if (a.kind === "openOptions") setOptionsOpen(true);
    else actions.onFix(a);
  };

  const real = vm.mode === "real";
  const pad = mobile ? "p-4" : "p-3";

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col bg-panel" data-ticket-mode={vm.mode}>
      {!mobile && (
        <div className="flex shrink-0 items-center gap-2 border-b border-border bg-panel-2 px-3 py-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-amber">Trade ticket</h2>
          <ModeChip real={real} />
          <button
            type="button"
            onClick={actions.onClose}
            className="ml-auto text-dim hover:text-ink"
            title="Close ticket"
            aria-label="Close ticket"
          >
            ✕
          </button>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col">
      <fieldset
        disabled={vm.locked}
        aria-hidden={vm.locked || undefined}
        className={`min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain ${pad} ${mobile ? "space-y-4" : "space-y-3"}`}
      >
        <HeaderCard vm={vm} mobile={mobile} onFix={onFix} marketHref={marketHref} onLeave={actions.onClose} />
        <StakeSection vm={vm} actions={actions} mobile={mobile} onFix={onFix} />
        <MoreOptions vm={vm} actions={actions} mobile={mobile} open={optionsOpen} setOpen={setOptionsOpen} onFix={onFix} priceProblem={priceProblem} />
      </fieldset>

      <div className={`shrink-0 space-y-2 border-t border-border bg-panel-2 ${mobile ? "p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" : "p-3"}`}>
        {generalProblems(vm.problems).map((p) => (
          <ProblemLine key={p.key} p={p} onFix={onFix} mobile={mobile} />
        ))}
        {unlockSlot}
        {vm.confirm ? <ConfirmCard vm={vm} actions={actions} mobile={mobile} /> : <PlaceButton vm={vm} onPlace={actions.onPlace} mobile={mobile} />}
      </div>

      {vm.overlay && <Overlay overlay={vm.overlay} vm={vm} actions={actions} mobile={mobile} />}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- header */

function ModeChip({ real }: { real: boolean }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${real ? "bg-down/15 text-down" : "bg-amber/15 text-amber"}`}
      title={real ? "Real money — routes to the venue" : "Paper — practice money"}
    >
      {real ? "Real" : "Paper"}
    </span>
  );
}

function HeaderCard({
  vm,
  mobile,
  onFix,
  marketHref,
  onLeave,
}: {
  vm: TicketVM;
  mobile: boolean;
  onFix: (a: FixAction) => void;
  marketHref?: string;
  onLeave: () => void;
}) {
  const venueProblem = problemAt(vm.problems, "venue");
  return (
    <div className={`rounded-lg border border-border bg-panel-2 ${mobile ? "p-3.5" : "p-3"}`}>
      <div className="flex items-start gap-2">
        <div className={`min-w-0 flex-1 break-words font-semibold leading-snug text-ink ${mobile ? "text-base" : "text-sm"}`}>{vm.header.title}</div>
        {mobile && <ModeChip real={vm.mode === "real"} />}
      </div>
      {(vm.header.sub || marketHref) && (
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-faint">
          {vm.header.sub && <span className="min-w-0 flex-1">{vm.header.sub}</span>}
          {marketHref && (
            <Link to={marketHref} onClick={onLeave} className={`ml-auto inline-flex shrink-0 items-center hover:text-amber ${mobile ? "min-h-11" : ""}`}>
              View market ›
            </Link>
          )}
        </div>
      )}
      <div className="mt-2.5 flex items-center gap-2.5">
        <VenueLogo name={vm.header.venueName} logoUrl={vm.header.venueLogoUrl} size={mobile ? 26 : 22} />
        <div className="min-w-0 flex-1">
          <div className={`break-words text-ink ${mobile ? "text-sm" : "text-[13px]"}`}>{vm.header.venueName}</div>
          {vm.connection && <ConnectionLine c={vm.connection} onFix={onFix} />}
        </div>
        <Tick className={`nums shrink-0 font-semibold text-ink ${mobile ? "text-xl" : "text-lg"}`} value={vm.header.priceText} />
      </div>
      {venueProblem && (
        <div className="mt-2">
          <ProblemLine p={venueProblem} onFix={onFix} mobile={mobile} />
        </div>
      )}
      {vm.hint && !venueProblem && (
        <button
          type="button"
          onClick={() => onFix(vm.hint!.fix.action)}
          className={`mt-2 flex w-full items-center gap-1.5 rounded-md border border-up/25 bg-up/5 px-2 text-left text-up hover:bg-up/10 ${mobile ? "min-h-[40px] text-xs" : "py-1 text-[11px]"}`}
        >
          <span className="min-w-0 flex-1">{vm.hint.text}</span>
          <span className="shrink-0 font-semibold">{vm.hint.fix.label} ›</span>
        </button>
      )}
    </div>
  );
}

function ConnectionLine({ c, onFix }: { c: NonNullable<TicketVM["connection"]>; onFix: (a: FixAction) => void }) {
  const dot = c.readiness === "ready" ? "bg-up" : c.readiness === "fund" ? "bg-amber" : "bg-faint";
  return (
    <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-faint">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} aria-hidden />
      <span className="nums">{c.text}</span>
      {c.fix && (
        <button type="button" onClick={() => onFix(c.fix!.action)} className="font-semibold text-amber hover:underline">
          {c.fix.label} ↗
        </button>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- stake */

function StakeSection({ vm, actions, mobile, onFix }: { vm: TicketVM; actions: TicketActions; mobile: boolean; onFix: (a: FixAction) => void }) {
  const stakeProblem = problemAt(vm.problems, "stake");
  if (vm.loading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Getting the price">
        <div>
          <Skeleton className="mb-1.5 h-3 w-12" />
          <Skeleton className={mobile ? "h-[50px] w-full" : "h-[38px] w-full"} />
          <div className="mt-2 flex gap-1.5">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className={mobile ? "h-9 flex-1" : "h-7 flex-1"} />
            ))}
          </div>
        </div>
        <Skeleton className={mobile ? "h-[86px] w-full" : "h-[74px] w-full"} />
      </div>
    );
  }
  const isAmount = vm.stake.unit === "amount";
  return (
    <div className="gb-fade space-y-3">
      <div>
        <label className="block">
          <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-dim">{vm.stake.label}</span>
          <div
            className={`flex items-center rounded-lg border bg-bg focus-within:border-amber ${stakeProblem?.tone === "block" ? "border-down/60" : "border-border-2"}`}
          >
            {isAmount && <span className={`pl-3 text-dim ${mobile ? "text-lg" : "text-base"}`}>$</span>}
            <input
              inputMode="decimal"
              autoComplete="off"
              value={vm.stake.text}
              onChange={(e) => actions.onStakeText(e.target.value)}
              onKeyDown={blockEnter}
              aria-label={isAmount ? "Stake in dollars" : "Contracts"}
              aria-invalid={stakeProblem?.tone === "block" || undefined}
              className={`nums w-full min-w-0 bg-transparent text-ink outline-none ${isAmount ? "pl-1" : "pl-3"} pr-3 ${mobile ? "py-3 text-lg" : "py-2 text-base"}`}
            />
            {!isAmount && <span className="pr-3 text-[11px] text-faint">contracts</span>}
          </div>
        </label>
        <div className="mt-2 flex gap-1.5">
          {vm.stake.chips.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => actions.onChip(c.value)}
              className={`nums flex-1 rounded-md border text-center transition-colors ${mobile ? "min-h-[40px] text-sm" : "py-1 text-xs"} ${
                c.active ? "border-amber/60 bg-amber/15 font-semibold text-amber" : "border-border-2 text-dim hover:border-border-2 hover:bg-raised hover:text-ink"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        {stakeProblem && (
          <div className="mt-2">
            <ProblemLine p={stakeProblem} onFix={onFix} mobile={mobile} />
          </div>
        )}
      </div>
      <QuoteBlock vm={vm} mobile={mobile} />
    </div>
  );
}

function QuoteBlock({ vm, mobile }: { vm: TicketVM; mobile: boolean }) {
  const [open, setOpen] = useState(false);
  const q = vm.quote;
  const soft = q.status === "checking";
  const tone = q.profitTone === "up" ? "text-up" : q.profitTone === "down" ? "text-down" : "text-dim";
  return (
    <div className={`rounded-lg border border-border bg-panel-2 ${mobile ? "px-3.5 py-3" : "px-3 py-2.5"}`}>
      <div className={`grid grid-cols-3 gap-2 ${soft ? "gb-checking" : ""}`} aria-live="polite" aria-busy={soft || undefined}>
        <QuoteCell label="You pay" value={q.pay} className="text-ink" mobile={mobile} />
        <QuoteCell label="Could win" value={q.win} className="text-ink" mobile={mobile} />
        <QuoteCell label="Profit" value={q.profit} className={tone} mobile={mobile} />
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px]">
        <QuoteTag status={q.status} />
        {q.details.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={`ml-auto text-dim hover:text-ink ${mobile ? "-my-2 py-2" : ""}`}
          >
            details <span aria-hidden className={`inline-block transition-transform duration-150 ${open ? "rotate-90" : ""}`}>›</span>
          </button>
        )}
      </div>
      {open && (
        <dl className="gb-fade mt-2 space-y-1 border-t border-border pt-2 text-[11px]">
          {q.details.map((d) => (
            <div key={d.label} className="flex justify-between gap-2">
              <dt className="text-faint">{d.label}</dt>
              <dd className="nums text-right text-dim">{d.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function QuoteCell({ label, value, className, mobile }: { label: string; value: string; className: string; mobile: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-faint">{label}</div>
      <Tick value={value} className={`nums block truncate font-semibold ${mobile ? "text-lg" : "text-base"} ${className}`} />
    </div>
  );
}

function QuoteTag({ status }: { status: TicketVM["quote"]["status"] }) {
  if (status === "checking") return <span className="text-faint">Checking…</span>;
  if (status === "exact")
    return (
      <span key="exact" className="gb-fade font-semibold text-up" title="Priced by the server for exactly this order">
        ✓ Exact
      </span>
    );
  if (status === "estimate") return <span className="text-faint" title="Estimated from the venue's rules">estimate</span>;
  return <span className="text-faint">Enter a stake to see the price</span>;
}

/* --------------------------------------------------------- more options */

function MoreOptions({
  vm,
  actions,
  mobile,
  open,
  setOpen,
  onFix,
  priceProblem,
}: {
  vm: TicketVM;
  actions: TicketActions;
  mobile: boolean;
  open: boolean;
  setOpen: (v: boolean) => void;
  onFix: (a: FixAction) => void;
  priceProblem: ProblemVM | null;
}) {
  const o = vm.options;
  if (vm.loading) return null;
  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`flex w-full items-center gap-2 px-3 text-left text-ink ${mobile ? "min-h-[48px] text-sm" : "py-2 text-xs"}`}
      >
        <span aria-hidden className={`inline-block text-[10px] text-faint transition-transform duration-150 ${open ? "rotate-90" : ""}`}>
          ▶
        </span>
        More options
        {o.summary && <span className="ml-auto min-w-0 truncate pl-2 text-[11px] text-faint">{o.summary}</span>}
      </button>
      {open && (
        <div className={`gb-fade space-y-4 border-t border-border ${mobile ? "p-3.5" : "p-3"}`}>
          <Segmented
            label="Size by"
            mobile={mobile}
            value={vm.stake.unit}
            options={[
              { value: "amount", label: "Dollars" },
              { value: "shares", label: "Contracts" },
            ]}
            onChange={(v) => actions.onUnit(v as StakeUnit)}
          />

          {o.canLimit && (
            <div>
              <Segmented
                label="Order type"
                mobile={mobile}
                value={o.orderType}
                options={[
                  { value: "market", label: "Market" },
                  { value: "limit", label: "Limit" },
                ]}
                onChange={(v) => actions.onOrderType(v as "market" | "limit")}
              />
              <p className="mt-1.5 text-[11px] leading-snug text-faint">{o.orderTypeCopy}</p>
            </div>
          )}

          <label className="block">
            <span className="mb-1.5 flex items-baseline justify-between gap-2">
              <span className="text-[11px] uppercase tracking-wide text-faint">{o.priceLabel}</span>
              {o.priceHint && <span className="nums text-[11px] text-faint">{o.priceHint}</span>}
            </span>
            <span className="flex items-center gap-2">
              <span
                className={`flex flex-1 items-center rounded-lg border bg-bg focus-within:border-amber ${o.priceInvalid || priceProblem?.tone === "block" ? "border-down/60" : "border-border-2"}`}
              >
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  value={o.priceText}
                  onChange={(e) => actions.onPriceText(e.target.value)}
                  onKeyDown={blockEnter}
                  aria-label={`${o.priceLabel} in cents`}
                  className={`nums w-full min-w-0 bg-transparent pl-3 text-ink outline-none ${mobile ? "py-2.5 text-base" : "py-1.5 text-sm"}`}
                />
                <span className="pr-3 text-dim">¢</span>
              </span>
              {!o.priceIsAuto && (
                <button type="button" onClick={() => onFix({ kind: "autoPrice" })} className="shrink-0 text-[11px] text-amber hover:underline">
                  auto
                </button>
              )}
            </span>
          </label>
          {priceProblem && <ProblemLine p={priceProblem} onFix={onFix} mobile={mobile} />}

          <label className={`flex items-start gap-2 text-dim ${mobile ? "text-xs" : "text-[11px]"}`}>
            <input
              type="checkbox"
              checked={o.verifyLiquidity}
              onChange={(e) => actions.onVerify(e.target.checked)}
              className={mobile ? "mt-0.5 h-4 w-4" : "mt-0.5"}
            />
            <span>
              Check the venue's live book first
              <span className="mt-0.5 block text-[10px] leading-snug text-faint">If the price has moved away, it's declined before it's placed.</span>
            </span>
          </label>

          {o.venues.length > 0 && (
            <div>
              <div className="mb-1.5 text-[11px] uppercase tracking-wide text-faint">Venue</div>
              <VenueList rows={o.venues} real={vm.mode === "real"} mobile={mobile} onPick={(id) => onFix({ kind: "select", partnerId: id })} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Segmented({
  label,
  value,
  options,
  onChange,
  mobile,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
  mobile: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[11px] uppercase tracking-wide text-faint">{label}</div>
      <div role="radiogroup" aria-label={label} className={`flex gap-1 rounded-md border border-border p-0.5 ${mobile ? "text-sm" : "text-xs"}`}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            onClick={() => onChange(o.value)}
            className={`flex-1 rounded px-2 text-center transition-colors ${mobile ? "min-h-[40px]" : "py-1"} ${
              value === o.value ? "bg-amber/20 font-semibold text-amber" : "text-dim hover:text-ink"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The ranked venue list: logo, price, best badge, and in Real its ready/connect state. */
export function VenueList({ rows, real, mobile, onPick }: { rows: VenueRowVM[]; real: boolean; mobile: boolean; onPick: (partnerId: string) => void }) {
  return (
    <div role="radiogroup" aria-label="Venue" className="space-y-1">
      {rows.map((v) => (
        <button
          key={v.partnerId}
          type="button"
          role="radio"
          aria-checked={v.selected}
          disabled={v.blocked}
          onClick={() => !v.selected && onPick(v.partnerId)}
          title={v.reason ?? undefined}
          className={`flex w-full items-center gap-2 rounded-md border px-2 text-left transition-colors ${mobile ? "min-h-[48px]" : "min-h-[36px] py-1"} ${
            v.selected ? "border-amber/50 bg-amber/10" : "border-transparent hover:bg-raised"
          } ${v.blocked ? "cursor-not-allowed opacity-45" : ""}`}
        >
          <VenueLogo name={v.name} logoUrl={v.logoUrl} size={mobile ? 20 : 18} />
          <span className="min-w-0 flex-1">
            <span className={`flex items-center gap-1.5 text-ink ${mobile ? "text-sm" : "text-xs"}`}>
              <span className="truncate">{v.name}</span>
              {v.best && <span className="shrink-0 rounded bg-up/15 px-1 text-[9px] font-semibold uppercase text-up">best</span>}
            </span>
            {v.blocked && v.reason ? (
              <span className="block truncate text-[10px] text-faint">{v.reason}</span>
            ) : real && v.readiness ? (
              <span className={`block text-[10px] ${v.readiness === "ready" ? "text-up" : v.readiness === "fund" ? "text-amber" : "text-faint"}`}>
                {v.readiness === "ready" ? `Ready${v.balanceText ? ` · ${v.balanceText}` : ""}` : v.readiness === "fund" ? "No funds" : "Not connected"}
              </span>
            ) : null}
          </span>
          <span className={`nums shrink-0 text-right font-semibold text-ink ${mobile ? "text-sm" : "text-xs"}`}>{v.priceText}</span>
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- problems */

function ProblemLine({ p, onFix, mobile }: { p: ProblemVM; onFix: (a: FixAction) => void; mobile: boolean }) {
  const tone =
    p.tone === "block" ? "border-down/40 bg-down/10 text-down" : p.tone === "warn" ? "border-amber/35 bg-amber/5 text-amber" : "border-border bg-panel-2 text-dim";
  return (
    <div className={`gb-fade rounded-md border px-2.5 py-1.5 ${tone} ${mobile ? "text-xs" : "text-[11px]"}`} role={p.tone === "block" ? "alert" : "status"}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <span className="min-w-[60%] flex-1 leading-snug" title={p.code}>
          {p.message}
        </span>
        {p.fixes.length > 0 && (
          <span className="flex flex-wrap gap-1.5">
            {p.fixes.map((f) => (
              <FixButton key={f.label} f={f} onFix={onFix} mobile={mobile} />
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

function FixButton({ f, onFix, mobile }: { f: FixVM; onFix: (a: FixAction) => void; mobile: boolean }) {
  return (
    <button
      type="button"
      onClick={() => onFix(f.action)}
      className={`shrink-0 whitespace-nowrap rounded border border-current/50 px-2 font-semibold hover:bg-current/10 ${mobile ? "min-h-[32px] text-xs" : "py-0.5 text-[11px]"}`}
    >
      {f.label}
      {f.external ? " ↗" : ""}
    </button>
  );
}

/* ---------------------------------------------------------------- button */

function PlaceButton({ vm, onPlace, mobile }: { vm: TicketVM; onPlace: () => void; mobile: boolean }) {
  const b = vm.button;
  const real = b.tone === "real";
  return (
    <button
      type="button"
      onClick={onPlace}
      disabled={b.disabled}
      className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${
        mobile ? "min-h-[52px] py-2 text-[15px]" : "min-h-[40px] py-1.5 text-sm"
      } ${real ? "bg-down text-bg hover:bg-down/90" : "bg-amber text-bg hover:bg-amber/90"}`}
    >
      {b.spinner && <Spinner />}
      <span className="nums min-w-0 text-center leading-tight">{b.label}</span>
    </button>
  );
}

function ConfirmCard({ vm, actions, mobile }: { vm: TicketVM; actions: TicketActions; mobile: boolean }) {
  const c = vm.confirm!;
  return (
    <div className="gb-fade rounded-lg border-2 border-down bg-down/10 p-3" role="alertdialog" aria-label="Confirm real-money order">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-down">Real money</div>
      <div className={`nums mt-1 font-semibold text-ink ${mobile ? "text-base" : "text-sm"}`}>{c.spend}</div>
      <div className="mt-1 flex items-baseline justify-between gap-2 text-xs text-dim">
        <span className="min-w-0 break-words">{c.outcome}</span>
        <span className="nums shrink-0 text-ink">{c.priceText}</span>
      </div>
      <div className="nums mt-1 text-xs text-up">{c.win}</div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={actions.onBack}
          className={`flex-1 rounded-md border border-border-2 font-semibold text-dim hover:bg-raised hover:text-ink ${mobile ? "min-h-[48px] text-sm" : "min-h-[36px] text-xs"}`}
        >
          Back
        </button>
        <button
          type="button"
          onClick={actions.onConfirm}
          className={`nums flex-[2] rounded-md bg-down font-semibold text-bg hover:bg-down/90 ${mobile ? "min-h-[48px] text-sm" : "min-h-[36px] text-xs"}`}
        >
          {c.confirmLabel} ▸
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- overlay */

function Overlay({ overlay, vm, actions, mobile }: { overlay: OverlayVM; vm: TicketVM; actions: TicketActions; mobile: boolean }) {
  return (
    <div className="gb-fade absolute inset-0 z-10 flex flex-col bg-panel/90 backdrop-blur-[2px]" role="status" aria-live="polite">
      <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto p-4">
        <Steps steps={overlay.steps} />
        <div className="mt-5">{overlay.result ? <ResultPanel r={overlay.result} vm={vm} actions={actions} mobile={mobile} /> : <SendingNote vm={vm} />}</div>
      </div>
    </div>
  );
}

function SendingNote({ vm }: { vm: TicketVM }) {
  return (
    <div className="text-center text-xs text-dim">
      <span className="nums">{vm.button.label.replace(/^Place /, "Placing ")}</span>
    </div>
  );
}

function Steps({ steps }: { steps: StepVM[] }) {
  return (
    <ol className="flex items-start justify-center">
      {steps.map((s, i) => (
        <li key={i} className="flex items-start">
          {i > 0 && (
            <span
              aria-hidden
              className={`mt-[9px] h-px w-4 shrink-0 transition-colors duration-300 ${steps[i - 1].state === "done" ? "bg-amber/60" : "bg-border-2"}`}
            />
          )}
          <span className="flex w-[62px] flex-col items-center gap-1.5 text-center">
            <StepDot state={s.state} />
            <span
              className={`text-[11px] leading-tight transition-colors duration-200 ${
                s.state === "active" ? "text-amber" : s.state === "done" ? "text-dim" : s.state === "failed" ? "text-down" : s.state === "unknown" ? "text-amber" : "text-faint"
              }`}
            >
              {s.label}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function StepDot({ state }: { state: StepVM["state"] }) {
  const base = "grid h-[18px] w-[18px] place-items-center rounded-full text-[10px] font-bold";
  if (state === "done") return <span className={`${base} bg-amber/20 text-amber`}>✓</span>;
  if (state === "failed") return <span className={`${base} bg-down/20 text-down`}>✕</span>;
  if (state === "unknown") return <span className={`${base} bg-amber/15 text-amber`}>?</span>;
  if (state === "active")
    return (
      <span className={`${base} border border-amber/60`}>
        <span className="pulse h-2 w-2 rounded-full bg-amber" />
      </span>
    );
  return <span className={`${base} border border-border-2`} />;
}

function ResultPanel({ r, vm, actions, mobile }: { r: ResultVM; vm: TicketVM; actions: TicketActions; mobile: boolean }) {
  const anchor = useRef<HTMLDivElement>(null);
  const btn = `rounded-md font-semibold ${mobile ? "min-h-[48px] text-sm" : "min-h-[38px] text-xs"}`;
  const primary = `${btn} ${vm.mode === "real" ? "bg-down text-bg hover:bg-down/90" : "bg-amber text-bg hover:bg-amber/90"}`;
  const secondary = `${btn} border border-border-2 text-ink hover:bg-raised`;

  switch (r.kind) {
    case "filled":
      return (
        <div ref={anchor} className="gb-fade text-center">
          <DrawnCheck />
          <div className={`mt-2 font-semibold text-up ${mobile ? "text-lg" : "text-base"}`}>{r.headline}</div>
          <div className="nums mt-1 text-xs text-ink">{r.line}</div>
          <div className="nums mt-0.5 text-xs text-dim">
            {r.win}
            {r.paper && <span className="ml-1.5 rounded bg-amber/15 px-1 text-[10px] font-semibold uppercase text-amber">paper</span>}
          </div>
          {r.warning && <div className="mt-2 rounded border border-down/50 bg-down/10 px-2 py-1 text-[11px] font-semibold text-down">⚠ {r.warning}</div>}
          <Details rows={r.details} />
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={actions.onPlaceAnother} className={`flex-1 ${secondary}`}>
              Place another
            </button>
            <button type="button" onClick={() => actions.onDone(anchor.current)} className={`flex-1 ${primary}`}>
              Done → Portfolio
            </button>
          </div>
        </div>
      );
    case "resting":
      return (
        <div ref={anchor} className="gb-fade">
          <div className="flex items-center justify-center gap-2 text-sm font-semibold text-ink">
            <span className="pulse h-2 w-2 shrink-0 rounded-full bg-blue" aria-hidden />
            <span className="nums">{r.headline}</span>
          </div>
          <FillBar pct={r.filledPct} tone="blue" />
          <div className="nums mt-1 text-center text-[11px] text-dim">{r.filledText}</div>
          {r.cancelNote && <div className="mt-2 text-center text-[11px] text-faint">{r.cancelNote}</div>}
          <Details rows={r.details} />
          <div className="mt-4 flex gap-2">
            {r.canCancel && (
              <button type="button" onClick={actions.onCancelResting} disabled={r.cancelBusy} className={`flex-1 ${secondary} disabled:opacity-50`}>
                {r.cancelBusy ? "Cancelling…" : "Cancel"}
              </button>
            )}
            <button
              type="button"
              onClick={() => actions.onLeaveWorking(anchor.current)}
              className={`flex-[2] ${btn} bg-blue/15 text-blue ring-1 ring-blue/50 hover:bg-blue/25`}
            >
              Leave it working
            </button>
          </div>
        </div>
      );
    case "partial":
      return (
        <div ref={anchor} className="gb-fade">
          <div className="nums text-center text-sm font-semibold text-amber">{r.headline}</div>
          <FillBar pct={r.filledPct} tone="amber" />
          <Details rows={r.details} />
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={() => actions.onDone(anchor.current)} className={`flex-1 ${secondary}`}>
              Done
            </button>
            {r.rest && (
              <button type="button" onClick={() => actions.onTryRest(r.rest!.amount, r.rest!.price)} className={`nums flex-[2] ${primary}`}>
                {r.rest.label}
              </button>
            )}
          </div>
        </div>
      );
    case "declined":
      return (
        <div className="gb-fade">
          <div className="text-center text-sm font-semibold text-down" title={r.code}>
            {r.headline}
          </div>
          <div className="mt-1 text-center text-[11px] text-faint">Nothing was placed. Your order is still filled in.</div>
          <Details rows={r.details} />
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={actions.onEdit} className={`flex-1 ${secondary}`}>
              Edit
            </button>
            <button type="button" onClick={() => actions.onRetry(r.retry.price)} className={`nums flex-[2] ${primary}`}>
              {r.retry.label}
            </button>
          </div>
        </div>
      );
    case "cancelled":
      return (
        <div ref={anchor} className="gb-fade text-center">
          <div className="text-sm font-semibold text-ink">{r.headline}</div>
          {r.line && <div className="mt-1 text-[11px] text-dim">{r.line}</div>}
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={actions.onPlaceAnother} className={`flex-1 ${secondary}`}>
              Place another
            </button>
            <button type="button" onClick={() => actions.onDone(anchor.current)} className={`flex-1 ${secondary}`}>
              Done
            </button>
          </div>
        </div>
      );
    case "checking":
      return (
        <div className="gb-fade text-center">
          <Spinner large />
          <div className="mt-2 text-sm text-ink">{r.headline}</div>
          <div className="mt-1 text-[11px] text-faint">The connection dropped after sending. We're looking it up in your orders — Place stays locked so nothing doubles.</div>
        </div>
      );
    case "unresolved":
      return (
        <div className="gb-fade text-center">
          <div className="text-sm font-semibold text-amber">{r.headline}</div>
          <div className="mt-1 text-[11px] leading-snug text-dim">{r.note}</div>
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={actions.onCheckAgain} className={`flex-1 ${secondary}`}>
              Check again
            </button>
            <Link to="/portfolio" onClick={actions.onClose} className={`flex flex-[2] items-center justify-center ${btn} bg-amber/15 text-amber ring-1 ring-amber/50 hover:bg-amber/25`}>
              Check Portfolio →
            </Link>
          </div>
        </div>
      );
  }
}

function FillBar({ pct, tone }: { pct: number; tone: "blue" | "amber" }) {
  const p = Math.max(0, Math.min(1, pct));
  return (
    <div
      className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border-2"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(p * 100)}
      aria-label="Filled"
    >
      <div className={`gb-bar h-full rounded-full ${tone === "blue" ? "bg-blue" : "bg-amber"}`} style={{ width: `${p * 100}%` }} />
    </div>
  );
}

function Details({ rows }: { rows: Array<{ label: string; value: string }> }) {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  return (
    <div className="mt-3 text-center">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="text-[10px] text-faint hover:text-dim">
        details <span aria-hidden className={`inline-block transition-transform duration-150 ${open ? "rotate-90" : ""}`}>›</span>
      </button>
      {open && (
        <dl className="gb-fade mt-1.5 space-y-1 rounded-md border border-border bg-panel-2 p-2 text-left text-[10px]">
          {rows.map((d, i) => (
            <div key={i} className="flex gap-2">
              <dt className="shrink-0 text-faint">{d.label}</dt>
              <dd className="nums min-w-0 flex-1 break-all text-right text-dim">{d.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function DrawnCheck() {
  return (
    <svg width="44" height="44" viewBox="0 0 28 28" className="mx-auto text-up" aria-hidden>
      <circle cx="14" cy="14" r="12" fill="none" stroke="currentColor" strokeWidth="1.6" className="gb-draw-ring" opacity="0.5" />
      <path d="M8.5 14.5l3.6 3.6 7.4-7.6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="gb-draw-check" />
    </svg>
  );
}

function Spinner({ large }: { large?: boolean }) {
  return (
    <span
      aria-hidden
      className={`gb-spin inline-block shrink-0 rounded-full border-2 border-current border-r-transparent ${large ? "mx-auto block h-6 w-6 text-amber" : "h-3.5 w-3.5"}`}
    />
  );
}

/** A value that ticks into place when it changes. */
function Tick({ value, className }: { value: string; className?: string }) {
  return (
    <span className={className}>
      <span key={value} className="gb-tick">
        {value}
      </span>
    </span>
  );
}

/** No implicit submit from a text field: placing is always an explicit button press. */
function blockEnter(e: React.KeyboardEvent<HTMLInputElement>): void {
  if (e.key === "Enter") e.preventDefault();
}
